import { useEffect, type ReactNode } from 'react';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { cn } from '../../lib/utils';

interface FormDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: ReactNode;
  loading?: boolean;
  maxWidth?: 'max-w-md' | 'max-w-lg' | 'max-w-xl' | 'max-w-2xl';
  /**
   * Disable the body's own scroll. Use when the child owns its scroll layout
   * and an outer scrollbar would be redundant (e.g., wizards with internal
   * step scrolling). Defaults to false.
   */
  disableBodyScroll?: boolean;
  /**
   * Pinned footer rendered below the scrollable body, inside the panel.
   * Use for action rows (Cancel / Save / Download) that must remain visible
   * while the body scrolls on short viewports. When omitted, no footer
   * region is rendered and the action row should live inside `children`.
   */
  footer?: React.ReactNode;
  /**
   * Pixel height applied to the panel so the dialog does not grow / shrink
   * as body content changes (e.g., a preview row count varies). The existing
   * `max-h-[80vh]` cap still applies, so on short viewports the panel
   * shrinks to the cap and the body scrolls. Without this prop the panel is
   * content-sized.
   */
  fixedHeight?: number;
  /** Responsive sizing or other panel-specific layout classes. */
  panelClassName?: string;
  /**
   * `sheet` docks the panel to the foot of the screen below the `sm`
   * breakpoint, full width with a grab handle, matching `SearchPopover`'s
   * phone sheet; at `sm` and up it is the usual centred modal. For pickers
   * and short forms opened from a phone, where a modal floating mid-screen
   * leaves the list cramped and the keyboard pushing it about.
   */
  presentation?: 'centred' | 'sheet';
  /**
   * Drop the body's padding and scroll so the child can run rows to the
   * panel's edges and own its scroll region. The child should inset its own
   * content with `px-4 sm:px-6` to line up with the header and footer.
   */
  flushBody?: boolean;
  /**
   * Rule under the title. Turn it off when the body opens with its own
   * header-like band (a search field) that draws the rule beneath itself, so
   * the title and that band read as one header with one rule.
   */
  headerDivider?: boolean;
  children: React.ReactNode;
}

export function FormDialog({
  open,
  onClose,
  title,
  description,
  loading = false,
  maxWidth = 'max-w-lg',
  disableBodyScroll = false,
  footer,
  fixedHeight,
  panelClassName,
  presentation = 'centred',
  flushBody = false,
  headerDivider = true,
  children,
}: FormDialogProps) {
  const sheet = presentation === 'sheet';
  const trapRef = useFocusTrap(open);

  // Escape key closes (unless loading)
  useEffect(() => {
    if (!open) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && !loading) {
        onClose();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, loading, onClose]);

  useBodyScrollLock(open);

  if (!open) return null;

  return (
    // `m-0` because the dialog does not portal: inside a `space-y-*` parent it
    // picks up a block-end margin, which shrinks a `fixed inset-0` box and
    // leaves a phone sheet floating above the bottom edge.
    <div
      className={cn(
        'animate-fade-in fixed inset-0 z-50 m-0 flex justify-center',
        sheet ? 'items-end sm:items-center sm:p-4' : 'items-center p-4',
      )}
      role="dialog"
      aria-modal="true"
      aria-labelledby="form-dialog-title"
    >
      <div
        className="fixed inset-0 bg-black/50"
        onClick={loading ? undefined : onClose}
      />
      {/* `text-left` for the same reason as ConfirmDialog's: this panel is
          `fixed` but does not portal, so it inherits `text-align` from whatever
          mounted it. A dialog owns its own alignment rather than borrowing its
          caller's. */}
      <div
        ref={trapRef}
        className={cn(
          'relative flex w-full flex-col border-border bg-bg-primary text-left shadow-lg sm:max-h-[80vh]',
          sheet
            ? 'animate-sheet-in max-h-[92dvh] rounded-t-2xl border-t sm:rounded-xl sm:border'
            : 'animate-dialog-in max-h-[90vh] rounded-xl border',
          maxWidth,
          panelClassName,
        )}
        style={{
          ...(fixedHeight ? { height: `${fixedHeight}px` } : {}),
          ...(sheet ? { paddingBottom: 'env(safe-area-inset-bottom)' } : {}),
        }}
      >
        {sheet && <div className="mx-auto mt-2 h-1 w-9 shrink-0 rounded-full bg-border sm:hidden" aria-hidden="true" />}
        {/* Header */}
        <div className={cn('flex shrink-0 items-start justify-between px-4 sm:px-6', headerDivider && 'border-b border-border', sheet ? 'pb-3 pt-2 sm:py-4' : 'py-4')}>
          <div>
            <h2 id="form-dialog-title" className="font-serif text-lg font-semibold text-secondary">
              {title}
            </h2>
            {description && (
              <p className="mt-1 text-sm text-text-muted">{description}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="ml-4 rounded-lg p-1 text-text-muted hover:bg-bg-subtle hover:text-secondary disabled:opacity-50"
            aria-label="Close dialog"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div
          className={cn(
            'flex-1',
            flushBody ? 'flex min-h-0 flex-col' : 'px-4 py-4 sm:px-6',
            flushBody || disableBodyScroll ? 'overflow-hidden' : 'overflow-y-auto',
          )}
        >
          {children}
        </div>

        {/* Footer (pinned). Outside the scrollable body so action rows stay
            visible when the body scrolls on short viewports. */}
        {footer && (
          <div className="shrink-0 border-t border-border px-4 py-3 sm:px-6">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
